<template>
  <div v-if="canLoad" class="form-group position-relative">
    <label v-if="label" class="font-size-12">{{ label }}</label>
    <div class="input-container d-flex">
      <input v-model="iValue" :disabled="!!(loadingUpload || loadingImages || loadingMoreImages || loadingGenerate)" type="text" class="form-control" placeholder="https://example.com" :style="{ flex: '1 1 auto' }" />
      <button :disabled="!!(loadingUpload || loadingImages || loadingMoreImages || loadingGenerate)" class="btn-upload" title="Upload" @click="selectFile">
        <b-spinner v-if="loadingUpload" small />
        <i v-else :class="uploadIcon" />
      </button>
      <button v-if="splashImage" :disabled="!!(loadingUpload || loadingImages || loadingMoreImages || loadingGenerate)" class="btn-upload" title="Images" @click="showImagesModal">
        <i :class="imagesIcon" />
      </button>
      <button v-if="user.agency.openai_api_key" :disabled="!!(loadingUpload || loadingImages || loadingMoreImages || loadingGenerate)" class="btn-upload" title="AI Image" @click="openAIModal">
        <i :class="magicIcon" />
      </button>
      <button :disabled="!!(loadingUpload || loadingImages || loadingMoreImages || loadingGenerate)" class="btn-upload btn-remove" title="Delete" @click="removeImage">
        <i :class="deleteIcon" />
      </button>
      <input ref="inputFile" type="file" name="upload" :style="{ display: 'none' }" accept="image/*" @change="uploadFile($event)" />
    </div>
    <div ref="splashImages" class="splash-images" :data-id="dataId" :style="{ display: 'none' }">
      <div class="input-container d-flex" :data-id="dataId">
        <input v-model="query" :disabled="!!(loadingUpload || loadingImages || loadingMoreImages || loadingGenerate)" type="text" class="form-control" placeholder="Search..." :style="{ flex: '1 1 auto' }" :data-id="dataId" @keyup.enter="searchImages" />
        <button class="btn-upload" title="Images" :disabled="!!(!query || loadingUpload || loadingImages || loadingMoreImages || loadingGenerate)" :data-id="dataId" @click="searchImages">
          <b-spinner v-show="loadingImages" small />
          <i v-show="!loadingImages" :class="`${magnifyingIcon} disabled`" />
        </button>
      </div>
      <div class="list-images" :data-id="dataId">
        <img v-for="(img, index) in splashImages" :key="index" :src="img.urls.regular" :data-id="dataId" @click="selectImage(img)" />
      </div>
      <div v-if="splashImages.length" class="text-center pt-3" :data-id="dataId">
        <b-button variant="outline-primary" size="sm" :disabled="!!(loadingUpload || loadingImages || loadingMoreImages || loadingGenerate)" :data-id="dataId" @click="loadMoreImages">
          <b-spinner v-show="loadingMoreImages" small />
          <span v-show="!loadingMoreImages" class="disabled">More images</span>
        </b-button>
      </div>
    </div>
    <b-modal v-model="showAIModal" title="Generate AI Image" hide-footer>
      <div class="mb-3">
        <label>Describe the image you want:</label>
        <textarea v-model="aiPrompt" class="form-control" rows="3" placeholder="Generate a stunning, high-quality image..." />
      </div>
      <div v-if="aiGeneratedImage" class="text-center mb-3">
        <img :src="aiGeneratedImage" alt="AI Preview" style="max-width: 100%; border-radius: 8px;" />
      </div>
      <div class="d-flex justify-content-between">
        <b-button :disabled="loadingGenerate || !aiPrompt" variant="primary" @click="generateAIImage">
          <b-spinner small v-if="loadingGenerate" />
          <span v-if="!loadingGenerate">{{ aiGeneratedImage ? 'Regenerate' : 'Generate' }}</span>
        </b-button>
        <b-button :disabled="loadingGenerate || !aiPrompt" v-if="aiGeneratedImage && !loadingGenerate" variant="success" @click="editAIImage">
          Edit Image
        </b-button>
        <b-button v-if="aiGeneratedImage && !loadingGenerate" variant="success" @click="keepAIImage">
          Use This Image
        </b-button>
        <b-button variant="secondary" @click="closeAIModal">Close</b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import './Styles/ImageInput2.scss'
import { DELETE_ICON, IMAGES_ICON, UPLOAD_ICON, MAGNIFYING_ICON, MAGIC_ICON } from '@src/icons'
export default {
  props: {
    label: { type: [String], default: '' },
    splashImage: { type: [Boolean], default: true },
    options2: { type: [Object], default: null, required: true },
    elementId: { type: String, default: null, required: true },
    valueKey: { type: String, default: null, required: true }
  },
  data() {
    return {
      deleteIcon: DELETE_ICON,
      imagesIcon: IMAGES_ICON,
      uploadIcon: UPLOAD_ICON,
      magnifyingIcon: MAGNIFYING_ICON,
      magicIcon: MAGIC_ICON,
      loadingImages: false,
      loadingMoreImages: false,
      splashImages: [],
      query: '',
      page: 1,
      timer: null,
      loadingGenerate: false,
      showAIModal: false,
      aiPrompt: '',
      aiGeneratedImage: '',
    }
  },
  computed: {
    user() { return this.$store.getters['auth/user'] },
    canLoad() {
      if (this.options2[this.elementId] && this.options2[this.elementId].hasOwnProperty(this.valueKey)) {
        return true
      }
      return false
    },
    dataId() {
      return `${this.elementId}:${this.valueKey}`
    },
    imageUploadStatus2: {
      get() { return this.$store.getters['utility/imageUploadStatus2'] },
      set(value) { this.$store.dispatch('utility/setImageUploadStatus2', value) }
    },
    loadingUpload() {
      return this.imageUploadStatus2[`${this.elementId}:${this.valueKey}`] || false
    },
    iValue: {
      get() {
        return this.options2[this.elementId][this.valueKey]
      },
      set(newVal) {
        this.options2[this.elementId][this.valueKey] = newVal
        this.options2[this.elementId].Ratio = 0
        if (this.timer) {
          clearTimeout(this.timer)
        }
        this.timer = setTimeout(() => {
          let that = this
          let img = new Image()
          img.onload = function() {
            let ratio = 0
            if (this.width && this.height && !isNaN(this.width) && !isNaN(this.height) && parseFloat(this.width) > 0) {
              const width = parseFloat(this.width)
              const height = parseFloat(this.height)
              ratio = parseFloat(parseFloat(height / width).toFixed(2))
            }
            that.options2[that.elementId].Ratio = ratio
          }
          img.src = newVal
        }, 500)
      }
    }
  },
  beforeDestroy() {
    document.removeEventListener('click', this.toggleSplashImages)
  },
  methods: {
    setImageUploadStatus2(value) {
      this.imageUploadStatus2 = value
    },
    selectFile() {
      this.$refs.inputFile.click()
    },
    uploadFile(event) {
      if (!event.target.files.length) return

      let imageParams = new FormData()
      imageParams.append('file', event.target.files[0])
      imageParams.append('resize-image', false)
      imageParams.append('elementId', this.elementId)
      imageParams.append('valueKey', this.valueKey)

      this.setImageUploadStatus2({
        ...this.imageUploadStatus2,
        [`${this.elementId}:${this.valueKey}`]: true
      })
      this.$store
        .dispatch('image/uploadImage', imageParams)
        .then((response) => {
          if (response.url && this.options2 && this.options2[response.elementId]) {
            this.options2[response.elementId][response.valueKey] = response.url
            this.options2[response.elementId].Ratio = response.Ratio || 0
          }
          this.setImageUploadStatus2({
            ...this.imageUploadStatus2,
            [`${response.elementId}:${response.valueKey}`]: false
          })
        })
        .catch((callParams) => {
          const elementId = callParams.get('elementId')
          const valueKey = callParams.get('valueKey')
          this.setImageUploadStatus2({
            ...this.imageUploadStatus2,
            [`${elementId}:${valueKey}`]: false
          })
        })
    },
    loadMoreImages() {
      this.page += 1
      this.loadingMoreImages = true
      this.$store
        .dispatch('image/getUnsplashImages', { query: this.query, page: this.page })
        .then((images) => {
          this.splashImages = [ ...this.splashImages, ...images ]
          this.loadingMoreImages = false
        })
        .catch(() => this.loadingMoreImages = false)
    },
    searchImages() {
      this.page = 1
      this.getImages()
    },
    getImages() {
      this.splashImages = []
      this.loadingImages = true
      this.$store
        .dispatch('image/getUnsplashImages', { query: this.query, page: this.page })
        .then((images) => {
          this.splashImages = images
          this.loadingImages = false
          if (this.$refs.splashImages) this.$refs.splashImages.style.display = 'block'
        })
        .catch(() => this.loadingImages = false)
    },
    selectImage(img) {
      let Ratio = 0
      if (img.width && img.height) {
        Ratio = parseFloat(img.height / img.width)
        Ratio = parseFloat(Ratio.toFixed(2))
      }
      this.options2[this.elementId][this.valueKey] = img.urls.regular
      this.options2[this.elementId].Ratio = Ratio
    },
    toggleSplashImages(event) {
      const dataId = event.target.getAttribute('data-id')
      if (!dataId) {
        if (this.$refs.splashImages) this.$refs.splashImages.style.display = 'none'
        document.removeEventListener('click', this.toggleSplashImages)
      }
    },
    removeImage() {
      this.options2[this.elementId][this.valueKey] = null
      this.options2[this.elementId].Ratio = 0
    },
    showImagesModal(event) {
      event.stopPropagation()
      document.addEventListener('click', this.toggleSplashImages)
      if (this.$refs.splashImages) this.$refs.splashImages.style.display = 'block'
    },
    openAIModal() {
      this.showAIModal = true
      this.aiGeneratedImage = this.options2[this.elementId][this.valueKey]
    },
    closeAIModal() {
      this.showAIModal = false
      this.aiGeneratedImage = ''
    },
    async generateAIImage() {
      if (!this.aiPrompt) return

      this.loadingGenerate = true
      try {
        const response = await this.$store.dispatch('image/generateAIImage', {
          image_prompt: this.aiPrompt,
          elementId: this.elementId,
          valueKey: this.valueKey,
          agency_id: this.user.agency.id,
        })

        if (response.url) {
          this.aiGeneratedImage = response.url
        } else {
          this.showErrorMessage('Image could not be generated. Please try again.')
        }
      } catch (error) {
        this.showErrorMessage('Image generation failed. Please try again later.')
      } finally {
        this.loadingGenerate = false
      }
    },
    async editAIImage() {
      if (!this.aiPrompt) return

      this.loadingGenerate = true
      try {
        const response = await this.$store.dispatch('image/editAIImage', {
          image_prompt: this.aiPrompt,
          agency_id: this.user.agency.id,
          origin_img: this.aiGeneratedImage,
        })

        if (response.url) {
          this.aiGeneratedImage = response.url
        } else {
          this.showErrorMessage('Image could not be generated. Please try again.')
        }
      } catch (error) {
        this.showErrorMessage('Image generation failed. Please try again later.')
      } finally {
        this.loadingGenerate = false
      }
    },
    keepAIImage() {
      this.options2[this.elementId][this.valueKey] = this.aiGeneratedImage
      this.options2[this.elementId].Ratio = 0
      this.closeAIModal()
    },
    showErrorMessage(message) {
      alert(message)
    },
  }
}
</script>